import React, { useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { UrgencyPill, Disclaimer } from "@/components/Badges";
import { MicButton } from "@/components/MicButton";
import { SensitiveInfoWarning } from "@/components/SensitiveInfoWarning";
import { useLang } from "@/context/LanguageContext";
import { Link } from "react-router-dom";

const STEPS_BY_LANG = {
  en: [
    { key: "what_happened", label: "In one or two sentences, what happened?", placeholder: "Example: I got a paper in the mail from a court in Cook County..." },
    { key: "document", label: "Did you receive a document? What does it look like?", placeholder: "Example: It's about 4 pages, has a case number, says 'Summons' at the top..." },
    { key: "court_name", label: "Is there a court name or county on the paper?", placeholder: "Example: Circuit Court of Cook County" },
    { key: "date", label: "Is there a date on the paper (hearing, deadline, or response date)?", placeholder: "Example: A hearing on March 14, or 'Respond within 30 days'" },
    { key: "case_type", label: "Do you know what kind of case this is (guess is okay)?", placeholder: "Example: Debt, eviction, family, small claims, or 'I don't know'" },
    { key: "help", label: "Do you already have legal help, or are you looking?", placeholder: "Example: I don't have a lawyer / I called legal aid" },
    { key: "barriers", label: "Is anything making this harder (language, disability, transportation, work, safety, tech)?", placeholder: "It's okay to say 'nothing right now.'" },
  ],
  es: [
    { key: "what_happened", label: "En una o dos oraciones, ¿qué pasó?", placeholder: "Ejemplo: Recibí un papel por correo de una corte en el condado de Cook..." },
    { key: "document", label: "¿Recibiste un documento? ¿Cómo se ve?", placeholder: "Ejemplo: Tiene unas 4 páginas, un número de caso, dice 'Summons' arriba..." },
    { key: "court_name", label: "¿Hay un nombre de corte o condado en el papel?", placeholder: "Ejemplo: Circuit Court of Cook County" },
    { key: "date", label: "¿Hay una fecha en el papel (audiencia, plazo o fecha de respuesta)?", placeholder: "Ejemplo: Audiencia el 14 de marzo, o 'Responder en 30 días'" },
    { key: "case_type", label: "¿Sabes qué tipo de caso es (adivinar está bien)?", placeholder: "Ejemplo: Deuda, desalojo, familia, small claims, o 'No sé'" },
    { key: "help", label: "¿Ya tienes ayuda legal, o la estás buscando?", placeholder: "Ejemplo: No tengo abogado / Llamé a ayuda legal" },
    { key: "barriers", label: "¿Algo lo hace más difícil (idioma, discapacidad, transporte, trabajo, seguridad, tecnología)?", placeholder: "Está bien decir 'nada por ahora.'" },
  ],
};

export default function Intake() {
  const { lang, t } = useLang();
  const STEPS = STEPS_BY_LANG[lang] || STEPS_BY_LANG.en;
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const current = STEPS[i];
  const isLast = i === STEPS.length - 1;

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        answers: STEPS.map((s) => ({ key: s.key, label: s.label, answer: answers[s.key] || "" })),
        language: lang,
      };
      const res = await api.post("/intake/analyze", payload);
      setResult(res.data);
    } catch (e) {
      alert(lang === "es" ? "Algo salió mal. Intenta de nuevo." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl sm:text-3xl font-bold">{t("intake.result_title")}</h1>
        </div>
        <UrgencyPill urgency={result.urgency} />
        <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 sm:p-6" data-testid="intake-summary">
          <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("intake.plain_summary")}</div>
          <p className="mt-2 text-lg leading-relaxed">{result.plain_summary}</p>
        </div>
        <ResultList title={t("intake.steps_title")} items={result.suggested_next_steps} testid="intake-steps" />
        <ResultList title={t("intake.resources_title")} items={result.suggested_resources} testid="intake-resources" />
        <ResultList title={t("intake.questions_title")} items={result.questions_you_may_want_to_ask} testid="intake-questions" />
        <Disclaimer>{result.disclaimer}</Disclaimer>
        <div className="flex flex-wrap gap-3 pt-2">
          <Link to="/deborah" data-testid="intake-goto-deborah" className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-5 rounded-xl inline-flex items-center gap-2">
            {t("intake.goto_deborah")} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/my-case" data-testid="intake-goto-mycase" className="pod-focus border-2 border-[#E4DDD3] hover:border-[#C85A32] font-bold py-3 px-5 rounded-xl inline-flex items-center gap-2">
            {t("intake.goto_mycase")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">
          {t("intake.progress_question")} {i + 1} {t("intake.progress_of")} {STEPS.length}
        </div>
        <div className="h-2 mt-2 rounded-full bg-[#F3EFE6] overflow-hidden" data-testid="intake-progress">
          <div className="h-full bg-[#C85A32] transition-all" style={{ width: `${((i + 1) / STEPS.length) * 100}%` }} />
        </div>
        <div className="mt-3"><SensitiveInfoWarning testid="intake-sensitive-warning" /></div>
      </div>

      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-6 sm:p-8">
        <label className="text-xl sm:text-2xl font-bold leading-snug text-[#1C2024] block">
          {current.label}
        </label>
        <textarea
          data-testid={`intake-answer-${current.key}`}
          rows={4}
          className="mt-5 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-4 text-lg bg-[#FBF9F5]"
          placeholder={current.placeholder}
          value={answers[current.key] || ""}
          onChange={(e) => setAnswers({ ...answers, [current.key]: e.target.value })}
        />
        <div className="flex items-center justify-between mt-3 gap-2">
          <p className="text-sm text-[#4A525A]">
            {t("intake.take_time")}
          </p>
          <MicButton
            testid={`intake-mic-${current.key}`}
            size="sm"
            label={t("intake.mic_label")}
            onTranscript={(tr) => setAnswers({
              ...answers,
              [current.key]: ((answers[current.key] || "") + (answers[current.key] ? " " : "") + tr).trim(),
            })}
          />
        </div>
      </div>

      <div className="flex justify-between items-center mt-6">
        <button
          data-testid="intake-back"
          disabled={i === 0}
          onClick={() => setI(Math.max(0, i - 1))}
          className="pod-focus inline-flex items-center gap-2 font-semibold text-[#4A525A] disabled:opacity-40 py-3 px-4"
        >
          <ArrowLeft className="w-4 h-4" /> {t("intake.back")}
        </button>
        {!isLast && (
          <button
            data-testid="intake-next"
            onClick={() => setI(i + 1)}
            className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2"
          >
            {t("intake.next")} <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {isLast && (
          <button
            data-testid="intake-submit"
            onClick={submit}
            disabled={loading}
            className="pod-focus bg-[#2C4A3E] hover:bg-[#1F382E] text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2 disabled:opacity-70"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t("intake.submit")}
          </button>
        )}
      </div>
    </div>
  );
}

function ResultList({ title, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 sm:p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{title}</div>
      <ul className="mt-3 space-y-2">
        {items.map((it, idx) => (
          <li key={`${testid}-${idx}-${(it || "").slice(0, 12)}`} className="flex gap-3 text-base leading-relaxed">
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#FAF5EC] text-[#C85A32] font-bold text-sm flex items-center justify-center">
              {idx + 1}
            </span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
